import React, { useState, useEffect } from 'react'
import { FaCalendarDay, FaClipboardCheck, FaEnvelope, FaExclamationTriangle, FaFileSignature, FaRegClock, FaRegFlag, FaRegQuestionCircle, FaUserClock, FaVideo } from 'react-icons/fa'
import { Link } from 'react-router'
import { Stack, Box, Text, Heading, Badge, Icon, Card, Loading } from '@/components/lib'
import api from '../../api'
import useStore from '../../store'

// The API returns the user's local day boundaries, so a time is all that needs
// showing against an item in the Today block.
function timeOfDay (value, timeZone) {
  if (!value) return null
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone
  }).format(new Date(value))
}

function daysSince (value) {
  if (!value) return null
  return Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 86400000))
}

function PanelHeading ({ icon, children }) {
  return (
    <Stack direction='row' alignItems='center' gap={2}>
      <Icon as={icon} color='fg.muted' />
      <Heading size='sm'>{children}</Heading>
    </Stack>
  )
}

// One line in the Today block: a time on the left, what it is on the right.
function TodayRow ({ time, icon, title, meta, to, emphasis }) {
  return (
    <Stack
      as={to ? Link : undefined} to={to} direction='row' gap={3} alignItems='baseline'
      _hover={to ? { textDecoration: 'none', bg: 'bg.subtle' } : undefined}
      rounded='sm' px={1} mx={-1} py={1}
    >
      <Text fontSize='xs' color='fg.muted' fontVariantNumeric='tabular-nums' minW='38px'>
        {time || '—'}
      </Text>
      <Stack gap={0} flex={1} minW={0}>
        <Stack direction='row' alignItems='center' gap={2}>
          {icon && <Icon as={icon} boxSize={3} color='fg.muted' />}
          <Text fontSize='sm' fontWeight={emphasis ? 'semibold' : 'normal'} truncate>{title}</Text>
        </Stack>
        {meta && <Text fontSize='xs' color='fg.muted' truncate>{meta}</Text>}
      </Stack>
    </Stack>
  )
}

function Divider () {
  return <Box borderBottomWidth='1px' borderColor='border.muted' />
}

function ConsentRow ({ count, children }) {
  return (
    <Stack direction='row' alignItems='center' gap={2}>
      <Icon as={FaFileSignature} boxSize={3} color='fg.muted' />
      <Text fontSize='sm' color='fg.muted'>{count} {children}</Text>
    </Stack>
  )
}

function TodayPanel ({ today }) {
  const schedule = today?.schedule || []
  const tasks = today?.tasks || []
  const visits = today?.monitoringVisits || []
  const slipped = today?.justSlipped || []
  const consent = today?.consent
  const activity = today?.activity || {}
  const activityParts = [
    activity.joined > 0 && `${activity.joined} joined`,
    activity.responses > 0 && `${activity.responses} ${activity.responses === 1 ? 'response' : 'responses'}`,
    activity.messages > 0 && `${activity.messages} ${activity.messages === 1 ? 'message' : 'messages'}`,
    activity.queries > 0 && `${activity.queries} ${activity.queries === 1 ? 'query' : 'queries'}${activity.automaticQueries > 0 ? ` (${activity.automaticQueries} automatic)` : ''}`
  ].filter(Boolean)
  const isEmpty = !schedule.length && !tasks.length && !visits.length &&
    !slipped.length && !consent && !activityParts.length

  // today.from is the first instant of the user's day; naming that day means
  // reading it back in the same zone the API cut it in.
  const timeZone = today?.timezone || undefined
  const dayLabel = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone
  }).format(today?.from ? new Date(today.from) : new Date())

  return (
    <Stack gap={3}>
      <PanelHeading icon={FaCalendarDay}>Today</PanelHeading>
      <Card variant='subtle'>
        <Stack gap={3}>
          <Text fontSize='xs' color='fg.muted'>{dayLabel}</Text>

          {isEmpty &&
            <Text fontSize='sm' color='fg.muted'>
              {today?.upcomingCount > 0
                ? `Nothing scheduled today — ${today.upcomingCount} ${today.upcomingCount === 1 ? 'booking' : 'bookings'} in the next ${today.upcomingDays} days.`
                : 'Nothing scheduled today.'}
            </Text>}

          {schedule.map(item => (
            <TodayRow
              key={`${item.type}-${item.id}`}
              time={timeOfDay(item.startsAt, timeZone)}
              icon={item.type === 'meeting' ? FaVideo : undefined}
              title={item.title}
              // isMine marks a booking the user is personally on, as opposed to
              // one they can see because they cover the site or trial.
              emphasis={item.isMine}
              meta={[item.participantIdentifier, item.trialName].filter(Boolean).join(' · ')}
              to={`/trials/${item.trial}/participants/${item.participantId}`}
            />
          ))}

          {visits.map(visit => (
            <TodayRow
              key={visit.id}
              time={timeOfDay(visit.scheduledAt, timeZone)}
              icon={FaClipboardCheck}
              title={`Monitoring visit${visit.identifier ? ` ${visit.identifier}` : ''}`}
              meta={[visit.siteName, visit.trialName].filter(Boolean).join(' · ')}
              to={`/trials/${visit.trial}/sites/${visit.site}/monitoring`}
            />
          ))}

          {tasks.map(task => {
            const overdue = new Date(task.dueDate) < new Date(today.from)
            return (
              <TodayRow
                key={task.id}
                time={overdue ? 'Due' : timeOfDay(task.dueDate, timeZone)}
                title={task.title}
                emphasis
                meta={
                  <>
                    {overdue && <Badge colorPalette='red' size='xs' mr={1}>Overdue</Badge>}
                    {task.trialName}
                  </>
                }
                to={`/trials/${task.trial}/tasks`}
              />
            )
          })}

          {slipped.length > 0 &&
            <Stack gap={2}>
              <Divider />
              <Text fontSize='xs' color='fg.muted' fontWeight='medium'>
                Just fell behind
              </Text>
              {slipped.map(p => (
                <TodayRow
                  key={p.participant}
                  time={p.daysLateBy === 0 ? 'Today' : `${p.daysLateBy}d`}
                  icon={FaUserClock}
                  title={p.participantIdentifier}
                  meta={`${p.stageCount} ${p.stageCount === 1 ? 'stage' : 'stages'} · ${p.trialName}`}
                  to={`/trials/${p.trial}/participants/${p.participant}`}
                />
              ))}
              {today.justSlippedTotal > slipped.length &&
                <Text fontSize='xs' color='fg.muted'>
                  and {today.justSlippedTotal - slipped.length} more
                </Text>}
            </Stack>}

          {consent &&
            <Stack gap={2}>
              <Divider />
              <Text fontSize='xs' color='fg.muted' fontWeight='medium'>Consent</Text>
              {consent.inProgress > 0 &&
                <ConsentRow count={consent.inProgress}>
                  in progress now
                </ConsentRow>}
              {/* A session is only resumable for 24 hours, so one that lapsed
                  mid-way needs starting again — the actionable case. */}
              {consent.abandoned > 0 &&
                <ConsentRow count={consent.abandoned}>
                  abandoned part-way
                </ConsentRow>}
              {consent.pendingVerification > 0 &&
                <ConsentRow count={consent.pendingVerification}>
                  awaiting identity verification
                </ConsentRow>}
            </Stack>}

          {activityParts.length > 0 &&
            <Stack gap={2}>
              <Divider />
              <Text fontSize='xs' color='fg.muted'>
                {activityParts.join(' · ')} today
              </Text>
            </Stack>}
        </Stack>
      </Card>
    </Stack>
  )
}

// A single counter within a trial's "needs attention" card.
function AttentionRow ({ icon, colorPalette, count, children, to }) {
  return (
    <Stack
      as={Link} to={to} direction='row' alignItems='center' gap={2}
      _hover={{ textDecoration: 'none', bg: 'bg.subtle' }} rounded='sm' px={1} mx={-1} py={1}
    >
      <Icon as={icon} boxSize={3} colorPalette={colorPalette} color='colorPalette.fg' />
      {count !== undefined && <Badge colorPalette={colorPalette} size='sm'>{count}</Badge>}
      <Text fontSize='sm' color='fg.muted' truncate>{children}</Text>
    </Stack>
  )
}

function NeedsAttentionPanel ({ entries }) {
  if (!entries?.length) return null
  return (
    <Stack gap={3}>
      <PanelHeading icon={FaRegFlag}>Needs attention</PanelHeading>
      {entries.map(entry => {
        const oldestDays = daysSince(entry.oldestOpenQueryAt)
        return (
          <Card variant='subtle' key={entry.trial}>
            <Stack gap={2}>
              <Heading size='xs'>
                <Link to={`/trials/${entry.trial}`}>{entry.trialName}</Link>
              </Heading>

              {entry.participants?.behindSchedule > 0 &&
                <AttentionRow
                  icon={FaUserClock} colorPalette='orange'
                  count={entry.participants.behindSchedule}
                  to={`/trials/${entry.trial}/participants`}
                >
                  behind schedule
                </AttentionRow>}

              {entry.participants?.stalled > 0 &&
                <AttentionRow
                  icon={FaRegClock} colorPalette='orange'
                  count={entry.participants.stalled}
                  to={`/trials/${entry.trial}/participants`}
                >
                  no response in {entry.participants.stalledAfterDays || 14} days
                </AttentionRow>}

              {entry.unreadMessages > 0 &&
                <AttentionRow
                  icon={FaEnvelope} colorPalette='blue'
                  count={entry.unreadMessages}
                  to={`/trials/${entry.trial}/messages`}
                >
                  unread {entry.unreadMessages === 1 ? 'message' : 'messages'}
                </AttentionRow>}

              {entry.unassignedQueries > 0 &&
                <AttentionRow
                  icon={FaRegQuestionCircle} colorPalette='purple'
                  count={entry.unassignedQueries}
                  to={`/trials/${entry.trial}/queries`}
                >
                  unassigned {entry.unassignedQueries === 1 ? 'query' : 'queries'}
                </AttentionRow>}

              {oldestDays > 30 &&
                <AttentionRow
                  icon={FaRegClock} colorPalette='purple'
                  to={`/trials/${entry.trial}/queries`}
                >
                  oldest open query is {oldestDays} days old
                </AttentionRow>}

              {entry.needsDataTypes &&
                <AttentionRow icon={FaExclamationTriangle} colorPalette='red' to={`/trials/${entry.trial}/data`}>
                  No data types set up yet
                </AttentionRow>}

              {entry.needsStages &&
                <AttentionRow icon={FaExclamationTriangle} colorPalette='red' to={`/trials/${entry.trial}/stages`}>
                  No stages set up yet
                </AttentionRow>}
            </Stack>
          </Card>
        )
      })}
    </Stack>
  )
}

export default function HomeSidebar () {
  const { user } = useStore()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user?._id) return
    let cancelled = false
    setLoading(true)
    api.users.getHome(
      user._id,
      d => { if (!cancelled) { setData(d); setLoading(false) } },
      // A failed summary should never take the trial list down with it — the
      // column just doesn't appear.
      () => { if (!cancelled) { setData(null); setLoading(false) } }
    )
    return () => { cancelled = true }
  }, [user?._id])

  if (loading) return <Box py={4}><Loading /></Box>
  if (!data) return null

  return (
    <Stack gap={6}>
      <TodayPanel today={data.today} />
      <NeedsAttentionPanel entries={data.needsAttention} />
    </Stack>
  )
}
